import { useContext, useRef } from "react";
import Form from "react-bootstrap/Form";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";

//Common
import FormBackGround from "../common/Forms/FormBackGround/FormBackGround";
import FormHeader from "../common/Forms/FormHeader/FormHeader";
import ActionBar from "../common/ActionBar/ActionBar";
import { EMPTY_GUID } from "../../utility/constants/constants";
import useComponentGeneralStyles from "../styles";

//App
import { TextField, DatePicker, PrependTextField, Chip } from "../common/VFX-UI";
import { CampaignContextData, CampaignFormProps } from "../../types/campaign";
import { CampaignContext } from "./CampaignContainer";
import { Label } from "reactstrap";

const validationSchema = Yup.object().shape({
    name: Yup.string()
        .required("Name is required")
        .max(250, "Max 250 characters are allowed."),
    description: Yup.string()
        .required("Description is required")
        .max(1500, "Max 1500 characters are allowed."),
    code: Yup.string().required("Campaign Code is required"),
    organizationId: Yup.array()
        .min(1, "Organization is required")
        .required("Organization is required"),
    startDate: Yup.date().required("Start Date is required.").nullable(),
    endDate: Yup.date()
        .typeError("Please enter a valid date.")
        .nullable()
        .required("End date is required.")
        .test(
            "is-enddate-greater",
            "End date must be greater than Start date",
            function (value) {
                const { startDate } = this.parent as any;
                // If either date is not provided, let the required validators handle it
                if (!startDate || !value) return true;
                const start = new Date(startDate);
                const end = new Date(value);
                if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
                return end >= start;
            }
        ),
    creditCardEndDate: Yup.date()
        .nullable()
        .test(
            "is-creditcard-enddate-greater",
            "Credit Card End Date must be greater than Credit Card Start Date",
            function (value) {
                const { creditCardStartDate } = this.parent as any;
                if (!creditCardStartDate || !value) return true;
                const start = new Date(creditCardStartDate);
                const end = new Date(value);
                if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
                return end >= start;
            }
        ),
    checkEndDate: Yup.date()
        .nullable()
        .test(
            "is-check-enddate-greater",
            "Check End Date must be greater than Check Start Date",
            function (value) {
                const { checkStartDate } = this.parent as any;
                if (!checkStartDate || !value) return true;
                const start = new Date(checkStartDate);
                const end = new Date(value);
                if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
                return end >= start;
            }
        ),
    payrollDeductionEndDate: Yup.date()
        .nullable()
        .test(
            "is-payroll-enddate-greater",
            "Payroll Deduction End Date must be greater than Payroll Deduction Start Date",
            function (value) {
                const { payrollDeductionStartDate } = this.parent as any;
                if (!payrollDeductionStartDate || !value) return true;
                const start = new Date(payrollDeductionStartDate);
                const end = new Date(value);
                if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
                return end >= start;
            }
        ),
});

const CampaignForm = (props: CampaignFormProps) => {
    const { status, initialVal, onClose, handleOnSubmit } = props;
    const formRef = useRef<FormikProps<any>>(null);
    const campaignContext = useContext<CampaignContextData | null>(CampaignContext);
    const generalStyles = useComponentGeneralStyles();

    const saveInput = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    let lookupOptions: any = [];
    if (campaignContext?.organizations) {
        lookupOptions = campaignContext?.organizations?.map((item: any) => ({
            value: item.id,
            label: item.display,
        }));
    }

    return (
        <FormBackGround>
            <FormHeader
                title={initialVal.id !== EMPTY_GUID ? "Edit Campaign" : "New Campaign"}
                onClose={onClose}
            />
            <Formik
                onSubmit={(values) => {
                    values.organizationId = initialVal.organizationId;
                    handleOnSubmit(values);
                }}
                initialValues={initialVal}
                validationSchema={validationSchema}
                innerRef={formRef}
            >
                {({ values }) => (
                    <Form noValidate>
                        <div className="d-flex justify-content-end mb-3 me-3">
                            <Chip label={values.isActive ? "Active" : "Inactive"} />
                        </div>

                        <div className="mx-3">
                            <TextField isRequired={true} label="Name" type="text" name="name" />
                            <TextField
                                isRequired={true}
                                label="Description"
                                type="textarea"
                                name="description"
                            />
                        </div>
                        <Label style={generalStyles.sectionHeader} className="border-bottom my-3 pb-2 w-100">Information</Label>
                        <div className="mx-3">
                            <TextField
                                isRequired={true}
                                label="Campaign Code"
                                type="text"
                                name="code"
                            />
                            <TextField
                                label="Organization"
                                type="text"
                                name="organization"
                                disabled={true}
                            />
                            <div className="row">
                                <div className="col-md-6">
                                    <DatePicker
                                        type="date"
                                        label="Start Date"
                                        name="startDate"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <DatePicker
                                        type="date"
                                        label="End Date"
                                        name="endDate"
                                    />
                                </div>
                            </div>
                        </div>
                        <Label style={generalStyles.sectionHeader} className="border-bottom my-3 pb-2  w-100">Pledge Details</Label>
                        <div className="mx-3">
                            <PrependTextField
                                prepend="$"
                                type="number"
                                name="pledgeGoal"
                            />
                            <DatePicker
                                type="date"
                                label="Payroll Deduction Start Date"
                                name="donationStartDate"
                            />
                        </div>

                        <Label style={generalStyles.sectionHeader} className="border-bottom my-3 pb-2 w-100">Payment Types</Label>
                        <div className="mx-3">
                            <div className="row">
                                <div className="col-md-6">
                                    <DatePicker type="date" label="Credit Card Start Date" name="creditCardStartDate" />
                                </div>
                                <div className="col-md-6">
                                    <DatePicker type="date" label="Credit Card End Date" name="creditCardEndDate" />
                                </div>
                                <div className="col-md-6">
                                    <DatePicker type="date" label="Check Start Date" name="checkStartDate" />
                                </div>
                                <div className="col-md-6">
                                    <DatePicker type="date" label="Check End Date" name="checkEndDate" />
                                </div>
                                <div className="col-md-6">
                                    <DatePicker type="date" label="Payroll Deduction Start Date" name="payrollDeductionStartDate" />
                                </div>
                                <div className="col-md-6">
                                    <DatePicker type="date" label="Payroll Deduction End Date" name="payrollDeductionEndDate" />
                                </div>
                            </div>
                        </div>

                        <div className="mt-5">
                            <ActionBar
                                submitHandler={saveInput}
                                cancelHandler={onClose}
                                submitText="Submit"
                                cancelText="Cancel"
                                isLoading={status === "pending"}
                            ></ActionBar>
                        </div>
                    </Form>
                )}
            </Formik>
        </FormBackGround>
    );
};

export default CampaignForm;
